import { useCallback, useEffect, useRef, useState } from 'react';
import { ObjectDetector, FilesetResolver } from '@mediapipe/tasks-vision';

const DETECTION_INTERVAL = 350;
const PHONE_SCORE_THRESHOLD = 0.45;
const POSITIVE_FRAMES_REQUIRED = 4;
const NEGATIVE_FRAMES_REQUIRED = 6;

export default function useObjectDetector({
  webcamRef,
  cameraError,
  active,
  onPhoneDetected,
}) {
  const [ready, setReady] = useState(false);
  const detectorRef = useRef(null);
  const timerRef = useRef(null);
  const runningRef = useRef(false);

  const positiveFramesRef = useRef(0);
  const negativeFramesRef = useRef(0);
  const phoneDetectedRef = useRef(false);
  const violationRecordedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks('/mediapipe/wasm');

        const detector = await ObjectDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: '/models/efficientdet_lite0.tflite',
            // delegate: 'GPU',
          },
          runningMode: 'VIDEO',
          scoreThreshold: PHONE_SCORE_THRESHOLD,
          maxResults: 3,
        });

//         console.log('[MODEL READY]', {
//   detector,
//   modelPath: '/models/efficientdet_lite0.tflite',
// });

        if (!cancelled) {
          detectorRef.current = detector;
          setReady(true);
        } else {
          detector.close();
        }
      } catch (err) {
        console.warn('Object detector gagal init:', err);
        setReady(false);
      }
    };

    initialize();

    return () => {
      cancelled = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      try {
        detectorRef.current?.close?.();
      } catch (err) {
        console.warn('Gagal menutup object detector:', err);
      }

      detectorRef.current = null;
      setReady(false);
    };
  }, []);
  

  const checkObjects = useCallback(() => {
    const detector = detectorRef.current;
    const video = webcamRef.current?.video;

    if (!detector || !video || !active || cameraError) {
      return;
    }

    if (
      video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
      video.videoWidth <= 0 ||
      video.videoHeight <= 0
    ) {
      return;
    }

    if (runningRef.current) {
      return;
    }

    runningRef.current = true;

    try {
      const result = detector.detectForVideo(video, performance.now());
const detections = result?.detections || [];

// console.log('[OBJECT DETECTOR]', {
//   videoWidth: video.videoWidth,
//   videoHeight: video.videoHeight,
//   detections: detections.map((detection) => ({
//     categories: detection.categories?.map((category) => ({
//       name: category.categoryName,
//       score: Number(category.score || 0).toFixed(3),
//     })),
//     boundingBox: detection.boundingBox,
//   })),
// });

// const startedAt = performance.now();
// const elapsed = performance.now() - startedAt;

// console.log('[DETECT RESULT]', {
//   elapsedMs: elapsed.toFixed(1),
//   detections: result?.detections?.length,
//   rawResult: result,
// });

    const phoneDetected = detections.some((detection) =>
  detection.categories?.some((category) => {
    const label = String(category.categoryName || '')
      .toLowerCase()
      .trim();

    const isPhone =
  ['cell phone', 'phone', 'smartphone'].includes(label);

return isPhone && Number(category.score || 0) >= PHONE_SCORE_THRESHOLD;
  })
);

//       console.log('[OBJECT DETECTOR STATUS]', {
//   ready: !!detector,
//   active,
//   cameraError,
//   readyState: video.readyState,
//   videoWidth: video.videoWidth,
//   videoHeight: video.videoHeight,
// });

      if (phoneDetected) {
        positiveFramesRef.current += 1;
        negativeFramesRef.current = 0;

        if (
          !phoneDetectedRef.current &&
          positiveFramesRef.current >= POSITIVE_FRAMES_REQUIRED
        ) {
          phoneDetectedRef.current = true;

          if (!violationRecordedRef.current) {
            violationRecordedRef.current = true;
            onPhoneDetected?.();
          }
        }
      } else {
        negativeFramesRef.current += 1;
        positiveFramesRef.current = 0;

        if (negativeFramesRef.current >= NEGATIVE_FRAMES_REQUIRED) {
          phoneDetectedRef.current = false;
          violationRecordedRef.current = false;
        }
        if (detections.length > 0) {
  console.log(
    '[DETECTION CANDIDATES]',
    detections.flatMap((detection) =>
      (detection.categories || []).map((category) => ({
        label: category.categoryName,
        score: Number(category.score || 0).toFixed(3),
        box: detection.boundingBox,
      }))
    )
  );
}
      }
    } catch (err) {
      console.error('Object detection error:', err);
    } finally {
      runningRef.current = false;
    }
  }, [active, cameraError, onPhoneDetected, webcamRef]);

  useEffect(() => {
    if (!active || cameraError || !ready) {
      return undefined;
    }

    let cancelled = false;

    const runDetection = () => {
      if (cancelled) {
        return;
      }

      checkObjects();
      timerRef.current = setTimeout(runDetection, DETECTION_INTERVAL);
    };

    runDetection();

    return () => {
      cancelled = true;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = null;
    };
  }, [active, cameraError, ready, checkObjects]);

  useEffect(() => {
  if (active) return;

  positiveFramesRef.current = 0;
  negativeFramesRef.current = 0;
  phoneDetectedRef.current = false;
  violationRecordedRef.current = false;
}, [active]);

  return { objectDetectorReady: ready };
}